/**
 * CR-7472: the customer-view Division options on Purchasing → Deliveries.
 *
 * The options used to come only from the Helix-only company-groups list, so
 * every customer user — a group user included — opened the dropdown onto
 * "No results". A division is the CompanyGroup a customer site belongs to,
 * and the accessible sites the tab already loads carry it (`group_id` /
 * `group_name`), so the options are derived from the sites in the current
 * view, the way the Dashboard derives its Division list. That makes the list
 * follow the view level (Group → its divisions, Division → that division,
 * Site → the site's division) and the selected Country, and it can never
 * offer a division outside the user's view or access.
 */

/** A Company Site option as DeliveryTab builds it from the accessible sites. */
export interface DeliverySiteOption {
  value: string;
  label: string;
  /** The division (CompanyGroup id) the site belongs to. */
  group?: number | null;
  /** That division's name — the Division option's label. */
  groupName?: string | null;
  country?: string;
}

export interface DivisionOption {
  value: string;
  label: string;
}

/**
 * Distinct divisions of the given sites, sorted by name, optionally narrowed
 * to the divisions with a site in one country.
 *
 * @param sites - Sites already scoped to the current view.
 * @param country - The selected Company Country; empty for all countries.
 * @returns Options for the Division dropdown.
 *
 * @example
 * deriveDivisionOptions(viewScopedSites, filters.customerCountry)
 */
export function deriveDivisionOptions(
  sites: ReadonlyArray<DeliverySiteOption>,
  country = '',
): DivisionOption[] {
  const byId = new Map<string, string>();
  for (const s of sites) {
    if (s.group == null || !s.groupName) continue;
    if (country && s.country !== country) continue;
    byId.set(String(s.group), s.groupName);
  }
  return Array.from(byId, ([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
